import type { ColDef } from 'ag-grid-enterprise';

import { CaseTableTooltipHeader } from '@/components/CaseTableTooltipHeader';
import { caseAIStatusFormatter } from '@/pages/Case/Case.utils';
import {
  CASE_AI_STATUS_COLORS,
  CASE_SOC_REVIEW_COLORS,
  CASE_STATUS_COLORS
} from '@/pages/Cases/Cases.constants';
import type { CaseList } from '@/pages/Cases/Cases.types';
import type { Organization } from '@/pages/Organizations/Organizations.types';
import {
  categoryRenderer,
  chipRenderer,
  dateTimeRenderer,
  doubleStatusRenderer,
  idRenderer,
  priorityRenderer,
  textRenderer,
  triplePriorityRenderer,
  verdictRenderer
} from '@/utils/GridRenderer.utils';

export const getCaseColumnDefs = (
  selectedOrganization: Organization
): ColDef<CaseList>[] => [
  {
    cellRenderer: idRenderer,
    cellRendererParams: {
      path: '/case',
      colorAnchor: true,
      idFieldKey: 'soc_id'
    },
    field: 'soc_id',
    headerName: 'ID',
    headerTooltip: 'ID',
    sortable: true,
    width: 130,
    comparator: (a: string | null, b: string | null) => {
      const getNum = (val: string | null) => {
        if (!val) return 0;
        const parts = val.split('-');
        return Number(parts[1] ?? parts[0] ?? 0);
      };
      return getNum(a) - getNum(b);
    }
  },
  {
    cellRenderer: idRenderer,
    cellRendererParams: {
      path: '/ticket',
      colorAnchor: true
    },
    field: 'scope_ticket_id',
    headerName: 'Ticket ID',
    headerTooltip: 'Ticket ID',
    sortable: true,
    width: 100
  },
  {
    cellRenderer: chipRenderer,
    cellRendererParams: {
      colors: CASE_AI_STATUS_COLORS
    },
    valueGetter: (params) =>
      caseAIStatusFormatter(params.data?.ai_status || ''),
    field: 'ai_status',
    headerName: 'Status',
    headerTooltip: 'Tooltip for AI Status Column Header', // This will trigger the CaseTableTooltipHeader component, text itself doesn't matter
    tooltipComponent: CaseTableTooltipHeader,
    sortable: true,
    width: 150
  },
  {
    cellRenderer: textRenderer,
    field: 'soc_case_title',
    flex: 1,
    headerName: 'Title',
    headerTooltip: 'Title',
    minWidth: 300,
    sortable: true
  },
  {
    cellRenderer: verdictRenderer,
    field: 'ai_verdict',
    headerName: 'Verdict',
    headerTooltip: 'Verdict',
    sortable: true,
    width: 200
  },
  {
    cellRenderer: verdictRenderer,
    field: 'scope_verdict',
    headerName: 'Ticket Resolution',
    headerTooltip: 'Ticket Resolution',
    sortable: true,
    width: 200
  },
  {
    cellRenderer: priorityRenderer,
    field: 'priority',
    headerName: 'Priority',
    headerTooltip: 'Priority',
    sortable: true,
    width: 80
  },
  {
    cellRenderer: categoryRenderer,
    field: 'customer_category',
    headerName: 'Category',
    headerTooltip: 'Category',
    sortable: true,
    width: 200
  },
  {
    cellRenderer: dateTimeRenderer,
    field: 'created_at',
    headerName: 'Created At',
    headerTooltip: 'Created At',
    sortable: true,
    width: 180
  },
  {
    cellRenderer: textRenderer,
    cellRendererParams: {
      fieldKey: 'organization_name'
    },
    field: 'organization_name',
    headerName: 'Organization',
    headerTooltip: 'Organization',
    sortable: true,
    width: 180,
    hide: selectedOrganization.id !== 0
  }
];

export const getInternalCaseColumnDefs = (
  selectedOrganization: Organization
): ColDef<CaseList>[] => [
  {
    cellRenderer: idRenderer,
    cellRendererParams: {
      path: '/case-internal',
      colorAnchor: true,
      idFieldKey: 'soc_id'
    },
    field: 'soc_id',
    headerName: 'SOC ID',
    headerTooltip: 'SOC ID',
    sortable: true,
    width: 130,
    comparator: (a: string | null, b: string | null) => {
      const getNum = (val: string | null) => {
        if (!val) return 0;
        const parts = val.split('-');
        return Number(parts[1] ?? parts[0] ?? 0);
      };
      return getNum(a) - getNum(b);
    }
  },
  {
    cellRenderer: idRenderer,
    cellRendererParams: {
      path: '/ticket',
      colorAnchor: true
    },
    field: 'scope_ticket_id',
    headerName: 'Ticket ID',
    headerTooltip: 'Ticket ID',
    sortable: true,
    width: 100
  },
  {
    cellRenderer: doubleStatusRenderer,
    cellRendererParams: {
      status1: 'soc_status',
      status2: 'ai_status',
      status1Colors: CASE_STATUS_COLORS,
      status2Colors: CASE_AI_STATUS_COLORS
    },
    field: 'soc_status',
    headerName: 'SOC / AI Status',
    headerTooltip: 'Tooltip for SOC / AI Status Column Header', // This will trigger the CaseTableTooltipHeader component, text itself doesn't matter
    tooltipComponent: CaseTableTooltipHeader,
    sortable: true,
    width: 250
  },
  {
    cellRenderer: textRenderer,
    field: 'soc_case_title',
    flex: 1,
    headerName: 'Title',
    headerTooltip: 'Title',
    minWidth: 300,
    sortable: true
  },
  {
    cellRenderer: verdictRenderer,
    field: 'soc_verdict',
    headerName: 'SOC Verdict',
    headerTooltip: 'SOC Verdict',
    sortable: true,
    width: 250
  },
  {
    cellRenderer: verdictRenderer,
    field: 'ai_verdict',
    headerName: 'AI Verdict',
    headerTooltip: 'AI Verdict',
    sortable: true,
    width: 150
  },
  {
    cellRenderer: verdictRenderer,
    field: 'scope_verdict',
    headerName: 'Ticket Verdict',
    headerTooltip: 'Ticket Verdict',
    sortable: true,
    width: 150
  },
  {
    cellRenderer: chipRenderer,
    cellRendererParams: {
      colors: CASE_SOC_REVIEW_COLORS
    },
    field: 'soc_review_message',
    headerName: 'SOC Reviewed',
    sortable: true,
    width: 180
  },
  {
    cellRenderer: triplePriorityRenderer,
    cellRendererParams: {
      priority1: 'soc_priority',
      priority2: 'ticket_priority',
      priority3: 'ai_priority'
    },
    field: 'soc_priority',
    headerName: 'Queue / Ticket / AI Priority',
    headerTooltip: 'Queue / Ticket / AI Priority',
    sortable: true,
    width: 180
  },
  {
    cellRenderer: categoryRenderer,
    field: 'ai_category',
    cellRendererParams: {
      category: 'ai_category'
    },
    headerName: 'AI Category',
    headerTooltip: 'AI Category',
    sortable: true,
    width: 200
  },
  {
    cellRenderer: dateTimeRenderer,
    field: 'created_at',
    headerName: 'Created At',
    headerTooltip: 'Created At',
    sortable: true,
    width: 180
  },
  {
    cellRenderer: textRenderer,
    cellRendererParams: {
      fieldKey: 'organization_name'
    },
    field: 'organization_name',
    headerName: 'Organization',
    headerTooltip: 'Organization',
    sortable: true,
    width: 180,
    hide: selectedOrganization.id !== 0
  }
];
